import { useState, useEffect } from 'react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui'
import { TaskSelector } from './TaskSelector'
import { TimerControls } from './TimerControls'
import { useTimerStore, formatTimerDisplay, useAuthStore } from '@/stores'
import { useCreateTimeLog, useOnlineStatus } from '@/hooks'
import { offlineQueue } from '@/lib/offlineQueue'
import { WifiOff } from 'lucide-react'
import type { Task } from '@/api'

interface TimerProps {
  className?: string
}

export function Timer({ className }: TimerProps) {
  const {
    isRunning,
    taskId,
    taskTitle,
    elapsedSeconds,
    startTimer,
    stopTimer,
    reset,
    hydrate,
  } = useTimerStore()

  const createTimeLog = useCreateTimeLog()
  const isOnline = useOnlineStatus()
  const [selectedTask, setSelectedTask] = useState<Task | null>(null)

  // Hydrate timer on mount to restore persisted state
  useEffect(() => {
    hydrate()
  }, [hydrate])

  // If timer is running with a task, set it as selected
  useEffect(() => {
    if (taskId && taskTitle && !selectedTask) {
      // Create a minimal task object for display
      setSelectedTask({
        id: taskId,
        title: taskTitle,
      } as Task)
    }
  }, [taskId, taskTitle, selectedTask])

  const handleStart = () => {
    if (!selectedTask) return
    startTimer(selectedTask.id, selectedTask.title)
  }

  const queueTimeLog = async (payload: {
    task_id: string
    duration_mins: number
    source: 'manual'
  }) => {
    const token = useAuthStore.getState().token
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    await offlineQueue.enqueue('/api/v1/timelogs', {
      method: 'POST',
      body: JSON.stringify(payload),
      headers,
    })
  }

  const handleStop = async () => {
    const result = stopTimer()
    if (result && result.elapsedSeconds >= 60) {
      // Only log if at least 1 minute
      const durationMins = Math.ceil(result.elapsedSeconds / 60)
      const payload = {
        task_id: result.taskId,
        duration_mins: durationMins,
        source: 'manual' as const,
      }

      if (isOnline) {
        try {
          await createTimeLog.mutateAsync(payload)
        } catch (error) {
          console.error('Failed to log time, queuing for later:', error)
          await queueTimeLog(payload)
        }
      } else {
        // Queue for later when online
        await queueTimeLog(payload)
      }
    }
    setSelectedTask(null)
  }

  const handleReset = () => {
    reset()
    setSelectedTask(null)
  }

  const handleTaskSelect = (task: Task) => {
    setSelectedTask(task)
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          Timer
          {!isOnline && (
            <WifiOff className="h-4 w-4 text-yellow-500" aria-label="Offline" />
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Timer display */}
        <div className="text-center">
          <div className="font-mono text-4xl font-bold tracking-tight">
            {formatTimerDisplay(elapsedSeconds)}
          </div>
          {isRunning && taskTitle && (
            <p className="text-sm text-muted-foreground mt-2 truncate">
              Working on: {taskTitle}
            </p>
          )}
        </div>

        {/* Task selector */}
        <TaskSelector
          selectedTask={selectedTask}
          onSelect={handleTaskSelect}
          disabled={isRunning}
        />

        {/* Controls */}
        <div className="flex justify-center">
          <TimerControls
            isRunning={isRunning}
            onStart={handleStart}
            onStop={handleStop}
            onReset={handleReset}
            disabled={!selectedTask && !isRunning}
            size="lg"
          />
        </div>

        {/* Time logging note */}
        {isRunning && (
          <p className="text-xs text-muted-foreground text-center">
            {isOnline
              ? 'Time will be logged when you stop the timer'
              : 'Time will be saved locally and synced when online'}
          </p>
        )}
      </CardContent>
    </Card>
  )
}
